import * as EventTracker from '@src/analytics/EventTracker';
import { PublishRecordService } from '@src/services/publish/PublishRecordService';
import { PublishStatus } from '@src/types.ts/publishRecord';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import {
  isPublishRecordFinished,
  isPublishRecordStuck,
} from '@src/utility/publishRecord';
import { makeAutoObservable } from 'mobx';

import type { RootStore } from './RootStore';
import type {
  PublishRecord,
  PublishRecordUpdate,
} from '@src/types.ts/publishRecord';

/**
 * The corners the publish progress drawer can be docked to (PLAT-11083). No
 * corner of the app is unoccupied — top-right sits over the Creator Studio
 * toolbar's actions, top-left over the video selector, and the bottom pair over
 * the timeline — so the drawer's position is a per-user choice rather than a
 * third global default. Top-CENTER is deliberately absent: it belongs to
 * `ToastNotification`, which the drawer cannot out-layer.
 */
export const PUBLISH_DRAWER_CORNERS = [
  'top-right',
  'top-left',
  'bottom-left',
  'bottom-right',
] as const;
export type PublishDrawerCorner = (typeof PUBLISH_DRAWER_CORNERS)[number];
export const DEFAULT_PUBLISH_DRAWER_CORNER: PublishDrawerCorner = 'top-right';

// Mirrored to localStorage so the corner survives a full reload — a publish can
// outlive a refresh (`seedForStory` restores it), and coming back in the default
// corner would undo the user's choice mid-publish.
const DRAWER_CORNER_KEY = 'publishProgress.drawer.corner';

function readDrawerCorner(): PublishDrawerCorner {
  try {
    // Widened to unknown[] so the membership test accepts whatever storage hands
    // back. A membership test rather than a null check on purpose: one expression
    // covers missing, null, undefined, hand-edited, and stale-corner-set values.
    const known: readonly unknown[] = PUBLISH_DRAWER_CORNERS;
    const raw = localStorage.getItem(DRAWER_CORNER_KEY);
    return known.includes(raw)
      ? (raw as PublishDrawerCorner)
      : DEFAULT_PUBLISH_DRAWER_CORNER;
  } catch {
    return DEFAULT_PUBLISH_DRAWER_CORNER;
  }
}

/**
 * Successor to `VideoCreatorStore.renderingVideoIds` — the source of truth for
 * "which renders are in flight," keyed on `PublishRecord` ids instead of Video
 * ids so it can represent per-variant progress (a language × AR × resolution
 * cell), which the video-level set could not.
 *
 * `inFlightIds` drives the live `PublishRecord` subscription (watch the id set,
 * prune on the finishing push — NOT a status filter, so a record that finishes
 * doesn't drop out before its final `succeeded`/`outputJson` arrives). `records`
 * caches the latest known record per id for the Downloads panel. Persisted
 * `status` is the source of truth, so `seedForStory` restores progress across a
 * page refresh. Repository reads go through `PublishRecordService`.
 */
export class PublishTrackingStore {
  private logger = LogManager.getLogger('store:PublishTrackingStore');

  inFlightIds: string[] = [];
  records: Map<string, PublishRecord> = new Map();
  /**
   * Groups the persistent publish progress drawer displays (PLAT-10703).
   * Auto-watched by `trackRecords`, which covers both entry paths: a fresh
   * publish (the fanout service tracks before `publish()` resolves) and the
   * page-refresh restore (`seedForStory`). Dismissing the drawer just empties
   * this — the cached `records` stay for the Downloads panel.
   */
  watchedGroupIds: string[] = [];
  /**
   * Drawer expanded/collapsed. Store-backed (not component state) because
   * `RootLayout` remounts on every route change and the drawer must keep its
   * state across Content ⇄ Creator navigation.
   */
  drawerExpanded = false;
  /**
   * Which corner the drawer is docked to (PLAT-11083). Store-backed for the same
   * reason as `drawerExpanded` — `RootLayout` remounts per route — but unlike it,
   * this is a durable *preference*: it is mirrored to localStorage and is
   * deliberately preserved by `dismissDrawer()` and `reset()`.
   */
  drawerCorner: PublishDrawerCorner = readDrawerCorner();
  /**
   * Mounted inline header hosts — the clip-preview modal's header strip
   * (PLAT-10947). While at least one is mounted the floating drawer stands
   * down, so progress appears in exactly one place. A count, not a boolean, so
   * an overlapping mount/unmount (React remounting the modal, or two clip
   * previews in sequence) can't strand the drawer hidden.
   */
  headerHostCount = 0;
  /**
   * Did the watched batch finish *while* a header host was mounted (PLAT-11395)?
   * When it did, the modal already reported the outcome — the strip showed
   * "Complete" and the completion toast fired — so re-floating the drawer on
   * close would announce a finished publish a second time, with nothing left to
   * watch. `unregisterHeaderHost` dismisses it instead. Scoped to the host
   * session (set on the completion transition, cleared when the last host
   * unmounts) so a batch that completed *before* the modal opened is left alone,
   * and so React's StrictMode double-mount — which unregisters once with the
   * flag still false — can't dismiss anything.
   */
  private completedUnderHeaderHost = false;
  /**
   * Optimistic pending publish: the variant count of a fanout the user just
   * dispatched, set at click time so the drawer appears instantly — before the
   * fanout's record creates round-trip to Dato. Cleared when the real records
   * arrive (`trackRecords`) or the fanout fails before creating any
   * (`clearPendingPublish`).
   */
  pendingPublishCount: number | null = null;

  constructor(private rootStore: RootStore) {
    makeAutoObservable<this, 'rootStore'>(
      this,
      { rootStore: false },
      { autoBind: true },
    );
  }

  get hasInFlight(): boolean {
    return this.inFlightIds.length > 0;
  }

  /** Reactive: is any in-flight (unfinished) record targeting this video? The
   *  successor to `renderingVideoIds.includes(videoId)` for card "Publishing…". */
  isVideoInFlight(videoId: string): boolean {
    return this.inFlightIds.some(
      id => this.records.get(id)?.videoId === videoId,
    );
  }

  /** Records with a live render, watched or not. The clip modal's header strip
   *  reads this to re-surface its own clip's publish after a dismissal — see
   *  `watchGroups`. */
  get inFlightRecords(): PublishRecord[] {
    return this.inFlightIds
      .map(id => this.records.get(id))
      .filter((r): r is PublishRecord => !!r);
  }

  /** All records of the watched groups — the union the drawer aggregates. */
  get watchedRecords(): PublishRecord[] {
    if (this.watchedGroupIds.length === 0) return [];
    return Array.from(this.records.values()).filter(r =>
      this.watchedGroupIds.includes(r.groupId),
    );
  }

  get isDrawerVisible(): boolean {
    return this.pendingPublishCount !== null || this.watchedRecords.length > 0;
  }

  /**
   * A publish is actively running right now — the optimistic pre-record window,
   * or any watched record still unfinished. Narrower than `isDrawerVisible`,
   * which stays true for a finished batch left on screen. Drives hints that only
   * make sense mid-publish, e.g. the clip modal's "won't cancel publishing"
   * close tooltip (PLAT-10947).
   */
  get isPublishInProgress(): boolean {
    if (this.pendingPublishCount !== null) return true;
    const watched = this.watchedRecords;
    return watched.length > 0 && !watched.every(isPublishRecordFinished);
  }

  /** True once every watched record reached a terminal state — drives the
   *  "Complete" title and the auto-expand on completion. */
  get watchedAllFinished(): boolean {
    const watched = this.watchedRecords;
    return watched.length > 0 && watched.every(isPublishRecordFinished);
  }

  setDrawerExpanded(expanded: boolean): void {
    this.drawerExpanded = expanded;
  }

  /** Dock the drawer to a corner and remember it across reloads. One discrete
   *  click per change, so it persists immediately — no set/commit split. */
  setDrawerCorner(corner: PublishDrawerCorner): void {
    this.drawerCorner = corner;
    try {
      localStorage.setItem(DRAWER_CORNER_KEY, corner);
    } catch {
      // Best-effort persistence.
    }
  }

  /** True while an inline header strip is mounted — the floating drawer defers
   *  to it so publish progress is never shown twice (PLAT-10947). */
  get hasHeaderHost(): boolean {
    return this.headerHostCount > 0;
  }

  registerHeaderHost(): void {
    this.headerHostCount += 1;
  }

  /**
   * When the last host unmounts (the modal closed), a batch that finished under
   * it is dismissed rather than handed back to the floating drawer — see
   * `completedUnderHeaderHost`. Anything still running is handed over as before,
   * so an in-progress publish keeps its live drawer.
   *
   * `drawerExpanded` is otherwise deliberately untouched: it belongs to the
   * drawer alone (the strip keeps expansion local), so the drawer resumes with
   * whatever state it had.
   */
  unregisterHeaderHost(): void {
    this.headerHostCount = Math.max(0, this.headerHostCount - 1);
    if (this.headerHostCount > 0) return;
    const shouldDismiss = this.completedUnderHeaderHost;
    this.completedUnderHeaderHost = false;
    if (shouldDismiss && !this.isPublishInProgress) this.dismissDrawer();
  }

  /** Show the drawer immediately on the Publish click (optimistic, before the
   *  fanout's records exist). `variantCount` drives the "Preparing" copy. */
  beginPendingPublish(variantCount: number): void {
    this.pendingPublishCount = variantCount;
  }

  /** Roll back `beginPendingPublish` when the fanout failed before creating
   *  any records (otherwise the drawer would show "Preparing" forever). */
  clearPendingPublish(): void {
    this.pendingPublishCount = null;
  }

  /** Dismiss the progress drawer (X, persistent at every stage — PLAT-10796).
   *  Only hides the progress UI; the fan-out keeps running server-side and clip
   *  cards still reflect live status via `inFlightIds`. A later publish pushes a
   *  new group via `trackRecords` and the drawer reappears — in the same corner,
   *  because `drawerCorner` is a preference and is deliberately untouched here. */
  dismissDrawer(): void {
    this.watchedGroupIds = [];
    this.drawerExpanded = false;
    this.pendingPublishCount = null;
    this.completedUnderHeaderHost = false;
  }

  /**
   * In-session augment / seed: cache full records and add their ids to the watch
   * set. Called on dispatch (the just-created canonical record) and on seed.
   */
  trackRecords(records: PublishRecord[]): void {
    // The real records are here — the optimistic placeholder has done its job.
    if (records.length > 0) this.pendingPublishCount = null;
    for (const record of records) {
      this.records.set(record.id, record);
      if (isPublishRecordFinished(record)) {
        // Promote into the story-scoped history cache, which nothing else
        // updates: this store's `records` is reset on every story entry, so
        // without this the clips grid forgets a publish made this session
        // (PLAT-11591).
        this.rootStore.publishRecordsStore.mergeStoryRecord(record);
      }
      if (
        !isPublishRecordFinished(record) &&
        !this.inFlightIds.includes(record.id)
      ) {
        this.inFlightIds.push(record.id);
        // Surface this group in the progress drawer (PLAT-10703). Watching
        // here (not in the fanout/UI) means both dispatch and refresh-restore
        // paths light the drawer with no extra call sites.
        if (!this.watchedGroupIds.includes(record.groupId)) {
          this.watchedGroupIds.push(record.groupId);
        }
      }
    }
  }

  /**
   * Watch these groups again — idempotent, and the counterpart to
   * `dismissDrawer` (PLAT-11395). Dismissing stops watching *everything*, so a
   * publish that is still running has no surface left; reopening the clip it
   * belongs to has to bring that one back, in the clip's own header strip. Only
   * the caller knows which groups are the clip's, so the choice is made there —
   * this just re-adds them.
   */
  watchGroups(groupIds: readonly string[]): void {
    for (const groupId of groupIds) {
      if (!this.watchedGroupIds.includes(groupId)) {
        this.watchedGroupIds.push(groupId);
      }
    }
  }

  /**
   * Seed the watch set from persisted in-flight records on load/refresh. The
   * status filter is correct here (one-time query); the live subscription
   * watches the resulting id set instead.
   *
   * Stuck orphans — `queued` records whose render was never dispatched (a
   * create-then-dispatch that died offline / on a crash, or a non-2xx that
   * didn't throw) — are excluded from tracking (so no card hangs on
   * "Publishing…") and best-effort reconciled to `failed` in Dato so they aren't
   * re-seeded and history is honest. This heals orphans from any healthy load,
   * including ones the failing session couldn't clean up itself. Persist-only:
   * the records are never tracked, so no in-memory prune is needed. Best-effort:
   * a failed write is logged, never thrown.
   */
  async seedForStory(storyId: string): Promise<void> {
    if (!storyId) return;
    try {
      const service = new PublishRecordService(
        this.rootStore.datoClientStore.publishRecordRepository,
      );
      const inflight = await service.getInFlightForStory(storyId);

      const fresh: PublishRecord[] = [];
      const stuck: PublishRecord[] = [];
      for (const record of inflight) {
        (isPublishRecordStuck(record) ? stuck : fresh).push(record);
      }

      this.trackRecords(fresh);

      for (const record of stuck) {
        try {
          await service.markFailed(
            record.id,
            'Render never dispatched (stale queued record)',
          );
        } catch (err) {
          this.logger.error(
            'Failed to reconcile stuck publish record',
            extractErrorInfo(err),
          );
        }
      }
    } catch (err) {
      this.logger.error(
        'Failed to seed in-flight publish records',
        extractErrorInfo(err),
      );
    }
  }

  /**
   * Apply a live subscription batch: MERGE each lean update onto the full record
   * cached at seed/dispatch, prune ids that have finished (their final push is
   * now delivered), and return the resulting merged records so callers can react
   * to finished records.
   */
  applyRecords(updates: PublishRecordUpdate[]): PublishRecord[] {
    const wasAllFinished = this.watchedAllFinished;
    const merged: PublishRecord[] = [];
    for (const update of updates) {
      const existing = this.records.get(update.id);
      const next = { ...existing, ...update } as PublishRecord;
      this.records.set(update.id, next);
      merged.push(next);
      if (isPublishRecordFinished(next)) {
        this.inFlightIds = this.inFlightIds.filter(id => id !== update.id);
        // Fire per-variant analytics only on the first terminal transition
        // (avoid duplicate fires on subscription replays).
        if (!existing || !isPublishRecordFinished(existing)) {
          this.trackFinishedEvent(next);
        }
      }
    }
    this.expandIfJustCompleted(wasAllFinished);
    return merged;
  }

  /**
   * Mark a tracked record failed in memory and prune it from the in-flight set.
   * Used when a render is never dispatched (build/dispatch threw) so the card
   * stops showing "Publishing…" and the live subscription stops watching a dead
   * id. The persisted `failed` write is done separately through
   * `PublishRecordService`. Fires the per-variant failed analytics once, guarded
   * like `applyRecords` so a later subscription push can't double-fire.
   */
  markFailed(recordId: string, errorMessage: string): void {
    const wasAllFinished = this.watchedAllFinished;
    const existing = this.records.get(recordId);
    const next = {
      ...(existing ?? {}),
      id: recordId,
      publishStatus: PublishStatus.Failed,
      errorMessage,
    } as PublishRecord;
    this.records.set(recordId, next);
    this.inFlightIds = this.inFlightIds.filter(id => id !== recordId);
    if (!existing || !isPublishRecordFinished(existing)) {
      this.trackFinishedEvent(next);
    }
    this.expandIfJustCompleted(wasAllFinished);
  }

  /**
   * Auto-expand the drawer's variant list the moment the whole watched batch
   * first reaches a terminal state, so per-variant downloads surface without a
   * manual click. Fires only on the transition (guarded by the caller's
   * pre-state), so a user who then collapses the list keeps it collapsed.
   */
  private expandIfJustCompleted(wasAllFinished: boolean): void {
    if (!wasAllFinished && this.watchedAllFinished) {
      this.drawerExpanded = true;
      // Remember that the modal, not the drawer, is where this batch finished.
      if (this.hasHeaderHost) this.completedUnderHeaderHost = true;
    }
  }

  getRecord(id: string): PublishRecord | undefined {
    return this.records.get(id);
  }

  /** Clear on story switch (mirrors how renderingVideoIds is reset per story).
   *  `drawerCorner` is deliberately NOT cleared — where the user parked the drawer
   *  is a preference, not per-story state. */
  reset(): void {
    this.inFlightIds = [];
    this.records.clear();
    this.watchedGroupIds = [];
    this.drawerExpanded = false;
    this.pendingPublishCount = null;
    this.completedUnderHeaderHost = false;
  }

  /** Fire trackPublishCompleted or trackPublishFailed for a finished record. */
  private trackFinishedEvent(record: PublishRecord): void {
    const base = {
      story_id: record.storyId,
      group_id: record.groupId,
      publish_record_id: record.id,
      language: record.language,
      aspect_ratio: record.aspectRatio,
      resolution: record.resolution,
    };

    if (record.publishStatus === PublishStatus.Succeeded) {
      const durationMs =
        record.renderStartedAt && record.renderCompletedAt
          ? new Date(record.renderCompletedAt).getTime() -
            new Date(record.renderStartedAt).getTime()
          : undefined;
      EventTracker.trackPublishCompleted({ ...base, duration_ms: durationMs });
    } else {
      EventTracker.trackPublishFailed({
        ...base,
        error_message: record.errorMessage ?? undefined,
      });
    }
  }
}
